import { Timestamp } from "firebase/firestore";
import { z } from "zod";
import type { GameCrewMember, GameContact } from "./types.person";

export const SPORT_DEFAULTS = {
  Baseball: {
    icon: "⚾",
    surface: "Field",
    positions: ["Solo", "Plate", "Bases"],
  },
  Softball: {
    icon: "🥎",
    surface: "Field",
    positions: ["Solo", "Plate", "Bases"],
  },
  Basketball: {
    icon: "🏀",
    surface: "Court",
    positions: ["Referee", "Umpire 1", "Umpire 2"],
  },
  Football: {
    icon: "🏈",
    surface: "Field",
    positions: [
      "Referee",
      "Umpire",
      "Head Linesman",
      "Line Judge",
      "Back Judge",
      "Field Judge",
      "Side Judge",
    ],
  },
  Soccer: {
    icon: "⚽",
    surface: "Pitch",
    positions: ["Center Referee", "Assistant Referee 1", "Assistant Referee 2"],
  },
  Hockey: {
    icon: "🏒",
    surface: "Ice",
    positions: ["Referee", "Linesman 1", "Linesman 2"],
  },
  Volleyball: {
    icon: "🏐",
    surface: "Court",
    positions: ["First Referee", "Second Referee", "Scorer"],
  },
};

export const SPORT_ICONS = Object.entries(SPORT_DEFAULTS).map(
  ([name, { icon }]) => ({ name, icon }),
);

export const EXTERNAL_SCHEDULERS = [
  "Horizon",
  "Arbiter",
  "GameChanger",
  "Assignr",
];

export enum GameStatus {
  Paid = "Paid",
  Pending = "Pending",
  Upcoming = "Upcoming",
  Cancelled = "Cancelled",
  Missed = "Missed",
  Unknown = "Unknown",
}

export interface Game {
  id: string;
  dateTime: string | Timestamp;
  location: string;
  locationId?: string;
  organizationId: string;
  partner?: string;
  partnerId?: string;
  partners?: string[];
  partnerIds?: string[];
  /** Structured crew (officials with positions); used when available alongside or instead of partner/partners */
  crew?: GameCrewMember[];
  /** Override sport for crew/display when different from organization (e.g. no org for that sport). Values: baseball, softball, basketball, football, soccer, hockey, volleyball. */
  gameSportId?: string;
  /** Game level for crew size/positions: youth, highSchool, college, professional. */
  gameLevel?: string;
  /** Non-official contacts for this game (AD, coach, secretary, etc.) */
  contacts?: GameContact[];
  teams?: string;
  homeTeam?: string;
  awayTeam?: string;
  team1Id?: string;
  team2Id?: string;
  position: {
    [key: string]: boolean;
  };
  miles: number;
  /** When true, miles were manually set; do not overwrite when From/To changes. */
  milesIsManual?: boolean;
  duration?: number;
  /** Start address for mileage (roundtrip or first leg). */
  startLocation?: string;
  /** End address when trip is one-way (Start → Game → End). Omitted or same as start = roundtrip. */
  endLocation?: string;
  /** Miles from start to game (for display/audit). */
  milesStartToGame?: number;
  /** Miles from game to end (for display/audit). When roundtrip, equals milesStartToGame. */
  milesGameToEnd?: number;
  /** Set when user confirms trip on game day. */
  tripConfirmedAt?: string | Timestamp;
  /** Set when mileage is reconciled for tax/report. */
  mileageReconciledAt?: string | Timestamp;
  promisedAmount: number;
  paidAmount?: number;
  isDemoData?: boolean;
  paidDate?: string | Timestamp;
  estimatedPaymentDate?: string | Timestamp;
  paymentMethod?: string;
  notes?: string;
  status: GameStatus;
  cancellationReason?: string;
  done: boolean;
  paidFlag: boolean;
  gcalEventId?: string;
  weather?: GetWeatherOutput;
  mileageNeedsCalculation?: boolean; // Flag to indicate mileage needs to be calculated
  /** V3 mileage.noTravel — true when user marked same-day / no deductible trip. */
  mileageNoTravel?: boolean;
  importSource?:
    | "Manual"
    | "Arbiter"
    | "Horizon"
    | "GameChanger"
    | "Assignr"
    | "AI"
    | "Demo"; // Source of game creation
  importErrors?: number; // Number of import errors encountered
}

/** A contact associated with an organization (AD, assignor, secretary, etc.). */
export interface OrgContact {
  name: string;
  phone?: string;
  email?: string;
  role?: string; // e.g. "Athletic Director", "Assignor", "Secretary"
}

export interface Organization {
  id: string;
  name: string;
  sport: string;
  alternateNames?: string[];
  orgProfileName?: string; // User's alias/aka name for this organization (e.g., how their name appears in Arbiter, Horizon, etc.)
  levels?: { name: string; amount: number }[];
  ruleUrl?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  /** Additional contacts beyond the primary (AD, assignor, etc.). */
  contacts?: OrgContact[];
  notes?: string;
  calendarSearchTerm?: string;
  ruleset?: string;
  defaultPaymentMethod?: string;
  paymentAccountInfo?: string;
  paymentNotes?: string;
}

export interface Location {
  id: string;
  name: string;
  address?: string;
  /** Geocoded coordinates for distance/map use */
  coordinates?: { latitude: number; longitude: number };
  distance?: number;
  travelTime?: number;
  notes?: string;
}

export interface Partner {
  id?: string;
  name: string;
  phone?: string;
  email?: string;
  notes?: string;
  /** Organization IDs the partner is associated with. */
  organizations?: string[];
  /** Position code (e.g. PU, U1) to suggest when adding this partner to a crew. */
  defaultPositionCode?: string;
  totalGames?: number;
  upcomingGames?: number;
  gamesThisYear?: number;
  isActive?: boolean;
  createdAt?: string | Timestamp;
  isDemoData?: boolean;
}

export type UserCohort =
  | "alpha"
  | "beta"
  | "baseball"
  | "multi-sport"
  | "applicant"
  | "admin"
  | "demo"
  | "unknown";

export interface AppUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  // User engagement tracking
  lastLoginAt?: Timestamp | string;
  createdAt?: Timestamp | string;
  loginCount?: number;
  cohort?: UserCohort;
  // Email reconciliation
  alternateEmails?: string[];
  // Onboarding status
  invitationSent?: boolean;
  signedIn?: boolean;
  dataImported?: boolean;
  feedbackProvided?: boolean;
  formCompleted?: boolean;
  // Beta program tracking
  betaMeeting1?: boolean;
  betaMeeting2?: boolean;
  betaMeeting3?: boolean;
  notes?: string;
  // Beta application data
  betaApplication?: {
    timestamp?: string;
    state?: string;
    sports?: string;
    yearsExperience?: string;
    expectedGames?: string;
    confirmation?: string;
  };
  // User timezone (based on home address)
  timezone?: string;
  // Firebase Auth metadata (from Admin SDK)
  authMetadata?: {
    creationTime?: string;
    lastSignInTime?: string;
    emailVerified?: boolean;
    phoneNumber?: string;
    providers?: string[];
    disabled?: boolean;
    customClaims?: Record<string, any>;
    multiFactorEnabled?: boolean;
  };
}

export interface SeasonGoals {
  seasonYear: string; // e.g., "2025"
  sport?: string;
  targetGames?: number;
  targetRevenue?: number;
  drivingPreferences?: {
    maxDistance?: number;
    avoidHighTraffic?: boolean;
    optimizeForGames?: boolean;
    routeConstraints?: string[];
  };
  transcript?: string; // Full voice transcript
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface CoachingSuggestion {
  id: string;
  title: string;
  description: string;
  category: "calendar" | "locations" | "routes" | "organizations" | "general";
  priority: "high" | "medium" | "low";
}

export interface ActionItem {
  id: string;
  title: string;
  description?: string;
  completed: boolean;
  createdAt: Timestamp;
  completedAt?: Timestamp;
}

export interface UpcomingGame {
  gameId?: string;
  date?: string;
  time?: string;
  location?: string;
  organization?: string;
  sportLevel?: string;
  home?: string;
  away?: string;
  umpire?: string;
  partner?: string;
  site?: string;
  dateTime?: string;
  /** From Desired/curated import: pay → promisedAmount */
  promisedAmount?: number;
  /** From Desired/curated import */
  miles?: number;
  /** User position (e.g. "Plate", "Bases") from schedule import */
  userPosition?: string;
  /** Partner position from schedule import */
  partnerPosition?: string;
  /** Notes from schedule import */
  notes?: string;
  /** Teams array from some parsers */
  teams?: string;
  /** Organization ID from schedule import */
  organizationId?: string;
  /** Position map from Arbiter MHTML parser */
  position?: { [key: string]: boolean };
  /** Import source identifier */
  importSource?: string;
}

// Zod Schemas for AI flows
export const PartnerSchema = z.object({
  id: z.string().optional(),
  name: z.string().describe("The full name of the umpire."),
  email: z.string().optional().describe("The umpire's email address."),
  phone: z.string().optional().describe("The umpire's cell phone number."),
  organizations: z
    .array(z.string())
    .optional()
    .describe("A list of organizations the partner is associated with."),
  notes: z.string().optional().describe("Notes about the partner."),
  defaultPositionCode: z
    .string()
    .optional()
    .describe(
      "Position code (e.g. PU, U1) to suggest when adding this partner to a crew.",
    ),
  createdAt: z
    .string()
    .optional()
    .describe("When the partner was created (ISO string)."),
  totalGames: z
    .number()
    .optional()
    .describe("Total number of games the partner has umpired."),
  upcomingGames: z
    .number()
    .optional()
    .describe("Number of upcoming games for this partner."),
  gamesThisYear: z
    .number()
    .optional()
    .describe("Number of games this year for this partner."),
  isActive: z
    .boolean()
    .optional()
    .describe("Whether the partner is considered active."),
  isDemoData: z
    .boolean()
    .optional()
    .describe("Whether this partner was created as demo data."),
});

export const ParseScheduleInputSchema = z.object({
  photoDataUri: z
    .string()
    .optional()
    .describe(
      "A photo of the umpire schedule, as a data URI that must include a MIME type and use Base64 encoding. Expected format: 'data:<mimetype>;base64,<encoded_data>'.",
    ),
  csvContent: z
    .string()
    .optional()
    .describe(
      "CSV content of the schedule export (for Horizon/Arbiter CSV files).",
    ),
  fileType: z
    .enum(["image", "pdf", "csv", "xlsx"])
    .optional()
    .describe(
      "The type of file being parsed. Used to determine parsing strategy.",
    ),
  umpireName: z
    .string()
    .describe(
      "The name of the umpire to find in the schedule (e.g., 'A. Traub' or 'Traub').",
    ),
});
export type ParseScheduleInput = z.infer<typeof ParseScheduleInputSchema>;

export const UpcomingGameSchema = z.object({
  date: z.string().describe("The full date of the game, e.g., 'Sept. 9th'"),
  time: z
    .string()
    .describe("The time of the game, e.g., '14U-6:00' or '8m-7:15'"),
  location: z.string().describe("The location or field, e.g., 'JWP #2'"),
  umpire: z
    .string()
    .describe("The name of the umpire found in the cell, e.g., 'A. Traub'"),
  partner: z
    .string()
    .optional()
    .describe("The name of the partner umpire for this game"),
  userPosition: z
    .string()
    .optional()
    .describe("The position of the user (e.g., 'Plate', 'Bases', 'Umpire')"),
  partnerPosition: z
    .string()
    .optional()
    .describe("The position of the partner (e.g., 'Plate', 'Bases', 'Umpire')"),
});

export const ParseScheduleOutputSchema = z.object({
  games: z
    .array(UpcomingGameSchema)
    .describe("An array of games found for the specified umpire."),
});
export type ParseScheduleOutput = z.infer<typeof ParseScheduleOutputSchema>;

export const ParsePartnersInputSchema = z.object({
  photoDataUri: z
    .string()
    .describe(
      "A photo of the umpire roster, as a data URI that must include a MIME type and use Base64 encoding. Expected format: 'data:<mimetype>;base64,<encoded_data>'.",
    ),
});
export type ParsePartnersInput = z.infer<typeof ParsePartnersInputSchema>;

export const ParsePartnersOutputSchema = z.object({
  partners: z
    .array(PartnerSchema)
    .describe("An array of partners found in the roster."),
});
export type ParsePartnersOutput = z.infer<typeof ParsePartnersOutputSchema>;

export interface Calendar {
  id: string;
  summary: string;
  primary: boolean;
  accessRole: string;
}

export interface CalendarSyncSettings {
  calendarId: string;
  syncEnabled: boolean;
  lastSync?: string | Timestamp;
  syncStatus?: "syncing" | "success" | "error";
  syncError?: string | null;
}

export const SetCalendarSyncSettingsInputSchema = z.object({
  calendarId: z.string(),
  syncEnabled: z.boolean(),
});
export type SetCalendarSyncSettingsInput = z.infer<
  typeof SetCalendarSyncSettingsInputSchema
>;

const GCalEventSchema = z.object({
  title: z
    .string()
    .optional()
    .describe("The title or summary of the calendar event."),
  description: z
    .string()
    .optional()
    .describe("The full description or body of the calendar event."),
  location: z.string().optional().describe("The location of the event."),
  start: z
    .string()
    .optional()
    .describe("The start date and time of the event in ISO 8601 format."),
});

export const ImportFromCalendarInputSchema = z.object({
  events: z
    .array(GCalEventSchema)
    .describe("An array of raw Google Calendar event objects to be parsed."),
  existingPartners: z
    .array(z.string())
    .describe("A list of already known umpire partner names."),
  existingLocations: z
    .array(z.string())
    .describe("A list of already known game location names."),
});
export type ImportFromCalendarInput = z.infer<
  typeof ImportFromCalendarInputSchema
>;

export const ImportFromCalendarOutputSchema = z.object({
  dateTime: z
    .string()
    .describe(
      "The date and time of the game in ISO 8601 format (YYYY-MM-DDTHH:mm:ss.sssZ).",
    ),
  teams: z
    .string()
    .describe(
      'The teams playing, formatted as "Team A vs Team B". Defaults to "TBD vs TBD" if not found.',
    ),
  location: z
    .string()
    .describe(
      "The location of the game. Try to match to an existing location if possible.",
    ),
  partner: z
    .string()
    .optional()
    .describe(
      "The name of the partner umpire. Try to match to an existing partner if possible.",
    ),
  position_solo: z
    .boolean()
    .optional()
    .describe("True if the umpire is working alone."),
  position_plate: z
    .boolean()
    .optional()
    .describe("True if the umpire is at the plate."),
  position_bases: z
    .boolean()
    .optional()
    .describe("True if the umpire is at the bases."),
  notes: z
    .string()
    .optional()
    .describe("Any additional notes extracted from the event."),
});
export type ImportFromCalendarOutput = z.infer<
  typeof ImportFromCalendarOutputSchema
>;

export interface ExternalSyncSource {
  name: string;
  enabled: boolean;
  searchTerm: string;
}

export interface UserAddress {
  id: string;
  name: string;
  address: string;
  /** Default start address for new games and mileage calc. */
  isDefault: boolean;
  /** Default end address when trip is one-way. At most one address should have this true. */
  isDefaultEnd?: boolean;
}

/** User settings for game/travel defaults (stored in users/{uid}/settings/gameDefaults). */
export interface GameDefaults {
  defaultOrganizationId?: string;
  defaultPromisedAmount?: number;
  defaultPosition?: string;
  defaultPartnerId?: string;
  defaultDuration?: number;
  /** "roundtrip" = end same as start; "one-way" = use default end address. */
  defaultTripType?: "roundtrip" | "one-way";
  /** Address id to use as start for new games / mileage. */
  defaultStartAddressId?: string;
  /** Address id for end when one-way. null/omitted = roundtrip (same as start). */
  defaultEndAddressId?: string | null;
}

export interface Team {
  id: string;
  name: string;
  /** Organization this team belongs to. */
  organizationId?: string;
  coachName?: string;
  coachPhone?: string;
  coachEmail?: string;
  notes?: string;
}

export interface Expense {
  id: string;
  date: string | Timestamp;
  vendor: string;
  amount: number;
  description: string;
  notes?: string;
  gameId?: string;
  receiptUrl?: string;
}

/** A deal/coupon partnership shown on the Deals page. Stored in Firestore settings/dealsConfig. */
export interface Deal {
  id: string;
  title: string;
  description: string;
  details: string;
  imageUrl: string;
  imageHint: string;
  url: string;
  couponCode: string;
  discountAmount: number;
  discountType: "percent" | "fixed" | "custom";
  discountLabel?: string;
  active: boolean;
  sortOrder: number;
}

export const CalculateMileageInputSchema = z.object({
  startAddress: z.string().describe("The starting address for the trip."),
  endAddress: z.string().describe("The destination address for the trip."),
  tripType: z
    .enum(["one-way", "round-trip"])
    .optional()
    .default("round-trip")
    .describe(
      "Specifies whether to calculate for a 'one-way' or 'round-trip' journey.",
    ),
});
export type CalculateMileageInput = z.infer<typeof CalculateMileageInputSchema>;

export const CalculateMileageOutputSchema = z.object({
  miles: z.number().describe("The estimated driving distance in miles."),
  travelTime: z.number().describe("The estimated travel time in minutes."),
  reasoning: z
    .string()
    .describe("A brief explanation of how the distance was calculated."),
});
export type CalculateMileageOutput = z.infer<
  typeof CalculateMileageOutputSchema
>;

export const GetWeatherInputSchema = z.object({
  location: z
    .string()
    .describe(
      'The location (e.g., "Nashville, TN" or an address) to get the weather for.',
    ),
  dateTime: z
    .string()
    .describe("The ISO 8601 date and time for the weather forecast."),
});
export type GetWeatherInput = z.infer<typeof GetWeatherInputSchema>;

export const GetWeatherOutputSchema = z.object({
  condition: z
    .string()
    .describe(
      'A one or two word description of the weather condition (e.g. "Sunny", "Partly Cloudy", "Thunderstorms").',
    ),
  temperature: z.number().describe("The temperature in Fahrenheit."),
  precipitationChance: z
    .number()
    .describe("The chance of precipitation as a percentage (0-100)."),
  narrative: z
    .string()
    .describe("A short, one-sentence narrative of the weather forecast."),
  icon: z
    .enum(["sun", "cloud", "rain", "snow", "wind", "zap", "moon"])
    .describe("An icon name representing the weather condition."),
  location: z
    .string()
    .optional()
    .describe(
      'The city and state of the weather forecast (e.g., "Nashville, TN").',
    ),
  lastUpdated: z
    .string()
    .optional()
    .describe("ISO string of when the weather was last updated."),
  apiInput: z
    .any()
    .optional()
    .describe("The input parameters sent to the weather API."),
  rawResponse: z
    .any()
    .optional()
    .describe("The raw JSON response from the weather API."),
  airQuality: z
    .object({
      index: z.number().describe("Air Quality Index (0-500)"),
      category: z
        .string()
        .describe(
          'Air quality category (e.g., "Good", "Moderate", "Unhealthy")',
        ),
      dominantPollutant: z
        .string()
        .optional()
        .describe("The dominant pollutant affecting air quality"),
      description: z
        .string()
        .describe("Human-readable description of air quality conditions"),
    })
    .optional()
    .describe("Air quality information for the location"),
  pollen: z
    .object({
      tree: z.number().optional().describe("Tree pollen level (0-12)"),
      grass: z.number().optional().describe("Grass pollen level (0-12)"),
      weed: z.number().optional().describe("Weed pollen level (0-12)"),
      overall: z.number().describe("Overall pollen level (0-12)"),
      description: z
        .string()
        .describe("Human-readable description of pollen conditions"),
    })
    .optional()
    .describe("Pollen information for the location"),
  source: z
    .string()
    .optional()
    .describe(
      'The data source for the weather information (e.g., "Google Weather API", "Open-Meteo Historical")',
    ),
});
export type GetWeatherOutput = z.infer<typeof GetWeatherOutputSchema>;

export const FindLocationAddressInputSchema = z.object({
  locationName: z.string().describe("The name of the location to find."),
  searchContext: z
    .string()
    .describe(
      'A hint about the location, e.g. "a high school near Nashville, TN".',
    ),
});
export type FindLocationAddressInput = z.infer<
  typeof FindLocationAddressInputSchema
>;

export const FindLocationAddressOutputSchema = z.object({
  address: z
    .string()
    .nullable()
    .describe("The full address found for the location, or null if not found."),
  googleMapsName: z
    .string()
    .nullable()
    .optional()
    .describe(
      "The official organization/place name from Google Maps, or null if not found.",
    ),
  location: z
    .object({ lat: z.number(), lng: z.number() })
    .nullable()
    .optional()
    .describe("Coordinates of the found location."),
  distanceFromBiasMiles: z
    .number()
    .nullable()
    .optional()
    .describe("Distance in miles from the search bias center (homebase), if bias was used."),
  warning: z
    .string()
    .nullable()
    .optional()
    .describe("Warning message if the result was suspect (e.g. too far from homebase)."),
});
export type FindLocationAddressOutput = z.infer<
  typeof FindLocationAddressOutputSchema
>;

export const ParseReceiptInputSchema = z.object({
  photoDataUri: z
    .string()
    .describe(
      "A photo of a receipt, as a data URI that must include a MIME type and use Base64 encoding. Expected format: 'data:<mimetype>;base64,<encoded_data>'.",
    ),
});
export type ParseReceiptInput = z.infer<typeof ParseReceiptInputSchema>;

export const ParseReceiptOutputSchema = z.object({
  vendor: z.string().optional().describe("The name of the store or vendor."),
  date: z
    .string()
    .optional()
    .describe("The date of the transaction in YYYY-MM-DD format."),
  amount: z
    .number()
    .optional()
    .describe("The total amount of the transaction."),
  description: z
    .string()
    .optional()
    .describe("A brief, one-line description of the items purchased."),
});
export type ParseReceiptOutput = z.infer<typeof ParseReceiptOutputSchema>;
